import { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Collapse, Divider, Drawer, Fab, Grid, Typography } from '@mui/material';
import { useTranslation } from 'next-i18next';

import CourseDetail from '../courseDetail';

import ProductDetailCourseStyle from './style';

import CourseCardOnBundle from 'src/components/molecules/courseCardOnBundle';
import config from 'src/config';
import { ICourse } from 'src/interfaces';
import { IProductBundleItem } from 'src/interfaces/product';
import { LearningTypeId } from 'src/types/product';

export interface IProductDetailCourse {
  sortOrder: number;
  thumbnailImage: string;
  name: string;
}

export interface IProductInstructorCourseCard {
  profileImage: string;
  name: string;
}

type Props = {
  productBundleItems: IProductBundleItem[];
  enrollmentedBundleSkuIds?: number[];
  bundleName?: string;
};

const ProductDetailCourses = ({ productBundleItems, enrollmentedBundleSkuIds, bundleName }: Props) => {
  const { t } = useTranslation('common');
  const [isOpenBundleDetailDrawer, setIsOpenBundleDetailDrawer] = useState<boolean>(false);
  const [currentCourseDetail, setCurrentCourseDetail] = useState<ICourse>();
  const [currentCourseDetailIndex, setCurrentCourseIndex] = useState<number>(0);
  const [previousCourseDetail, setPreviousCourseDetail] = useState<ICourse>();
  const [nextCourseDetail, setNextCourseDetail] = useState<ICourse>();

  const mapBundleEnrollments = (enrollmentedBundleSkuIds: any, product_sku_id: any) => {
    if (enrollmentedBundleSkuIds) {
      return enrollmentedBundleSkuIds?.includes(product_sku_id);
    }
    return false;
  };

  const courses =
    productBundleItems?.map((product, index) => ({
      courseNo: index + 1,
      courseImage:
        product.product_skus?.products.course?.highlight_medium_image ||
        `${config.IMAGE_CDN_HOST}/assets/thumbnail-course-card.jpg`,
      courseName: product.product_skus?.products.name || ' ',
      instructors: product.product_skus?.products.course?.instructors_info || [],
      isEnrolled: mapBundleEnrollments(enrollmentedBundleSkuIds, product.product_sku_id),
      haveCertificate:
        product.product_skus.products.course?.have_certificate ||
        product.product_skus.products.learning_type_id === LearningTypeId.CPD,
      course: {
        ...product.product_skus.products.course,
        isEnrolled: mapBundleEnrollments(enrollmentedBundleSkuIds, product.product_sku_id),
      },
      isCpd: product.product_skus.products.course?.product.isCpd,
    })) || [];

  const onDisplayCourseDetail = (course: any, isEnrolled: boolean, index: number) => {
    setIsOpenBundleDetailDrawer(true);
    setCurrentCourseDetail({
      ...course,
      isEnrolled,
    });
    setCurrentCourseIndex(index);
  };

  const getPreviousAndNextCourse = () => {
    const objects = productBundleItems;

    const currentIndex = objects.findIndex(
      (object) => object.product_skus?.products.course?.id === currentCourseDetail?.id,
    );
    const previousObject = objects[currentIndex - 1];
    const nextObject = objects[currentIndex + 1];

    const previousCourse: any = previousObject && previousObject.product_skus.products.course;
    const nextCourse: any = nextObject && nextObject.product_skus.products.course;

    setPreviousCourseDetail({
      ...previousCourse,
      isEnrolled: mapBundleEnrollments(enrollmentedBundleSkuIds, previousObject?.product_sku_id),
    });
    setNextCourseDetail({
      ...nextCourse,
      isEnrolled: mapBundleEnrollments(enrollmentedBundleSkuIds, nextObject?.product_sku_id),
    });
  };

  const onNextCourse = () => {
    setCurrentCourseDetail(nextCourseDetail);
    setCurrentCourseIndex(currentCourseDetailIndex + 1);
  };
  const onPreviousCourse = () => {
    setCurrentCourseDetail(previousCourseDetail);
    setCurrentCourseIndex(currentCourseDetailIndex - 1);
  };

  useEffect(() => {
    if (currentCourseDetail) {
      getPreviousAndNextCourse();
    }
  }, [currentCourseDetail]);

  return (
    <ProductDetailCourseStyle>
      <Typography color="black" variant="h4" fontWeight="500">
        {t('product_detail.bundle.courses.titile', { courseAmount: courses.length })}
      </Typography>
      <Box mt={1} mb={2}>
        <Divider />
      </Box>
      {courses.map(
        (
          { courseNo, courseImage, courseName, instructors, isEnrolled, haveCertificate, course, isCpd },
          index: number,
        ) => (
          <div onClick={() => onDisplayCourseDetail(course, isEnrolled, index)}>
            <CourseCardOnBundle
              courseNo={courseNo}
              courseImage={courseImage}
              courseName={courseName}
              isEnrolled={isEnrolled}
              haveCertificate={haveCertificate}
              instructors={instructors}
              isCPD={isCpd}
            />
          </div>
        ),
      )}
      <Drawer
        PaperProps={{
          sx: {
            height: { md: '100%', lg: '95%' },
            width: { md: '100%', lg: '85%' },
            margin: 'auto',
            bottom: { md: '0px', lg: '15px' },
            borderRadius: { md: '0px', lg: '10px' },
            backgroundImage: 'linear-gradient(to bottom, rgb(0,0,0,0.6), white 10%)',
            overflowX: 'hidden',
          },
        }}
        BackdropProps={{
          style: {
            backgroundImage: 'linear-gradient(to bottom, rgb(0,0,0,0.6), transparent 10%)',
          },
        }}
        anchor="bottom"
        open={isOpenBundleDetailDrawer}
        onClose={() => setIsOpenBundleDetailDrawer(false)}
      >
        <CourseDetail
          course={currentCourseDetail}
          onNext={onNextCourse}
          onPrev={onPreviousCourse}
          totalCourse={productBundleItems.length}
          currentIndex={currentCourseDetailIndex}
          bundleName={bundleName}
        />
        <Fab
          onClick={() => setIsOpenBundleDetailDrawer(false)}
          sx={{
            position: 'fixed',
            right: { xs: 10, md: 30 },
            top: { xs: 38, md: 30 },
            height: { xs: '40px', md: '60px' },
            width: { xs: '40px', md: '60px' },
            color: 'white',
            bgcolor: 'rgb(0, 0, 0, 0.35)',
            boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.160784)',
            zIndex: 9999,
          }}
        >
          <CloseIcon className="close-icon" sx={{ color: '#BDBDBD', fontSize: { xs: '20px', md: '36px' } }} />
        </Fab>
      </Drawer>
    </ProductDetailCourseStyle>
  );
};

export default ProductDetailCourses;
